import { c as _c } from "react/compiler-runtime";
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Type } from '@repo/design-system/components/ui/type';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectExploreActionMenuPinId } from '@repo/store/maps/slices/clientContext';
import { selectCurrentMapInfo } from '@repo/store/maps/slices/mapData';
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import { HeroActionDrawer } from '../location-info/hero-action-drawer';
import { useLocationInfo } from '../location-info/hooks/useLocationInfo';
import { LocationActions } from '../location-info/location-actions';
import { LocationInfo } from '../location-info/location-info';
interface MapLocationContentProps {
  isMobilePane?: boolean;
  nearbyPins?: MapDataLocationFeature[];
  onPinSelect?: (id: string) => void;
  selectedPinId?: string;
}
export const MapLocationContent = t0 => {
  const $ = _c(49);
  const {
    isMobilePane,
    nearbyPins,
    onPinSelect,
    selectedPinId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["game_map"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useT(t1);
  const {
    selectedLocation
  } = useLocationInfo();
  const analytics = useAnalytics();
  const hostType = useHostType();
  const currentMapInfo = useAppSelector(selectCurrentMapInfo);
  const exploreActionMenuPinId = useAppSelector(selectExploreActionMenuPinId);
  const gameCoords = selectedLocation?.properties?.gameCoords;
  const isExploreActionMenu = isMobilePane && !!selectedLocation && exploreActionMenuPinId === selectedLocation.properties.externalId;
  let t2;
  if ($[1] !== nearbyPins || $[2] !== selectedPinId) {
    t2 = nearbyPins?.findIndex(p => p.properties.externalId === selectedPinId) ?? -1;
    $[1] = nearbyPins;
    $[2] = selectedPinId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const currentIndex = t2;
  const total = nearbyPins?.length ?? 0;
  let t3;
  if ($[4] !== analytics || $[5] !== currentIndex || $[6] !== currentMapInfo?.externalId || $[7] !== hostType || $[8] !== nearbyPins || $[9] !== onPinSelect || $[10] !== total) {
    t3 = () => {
      if (!nearbyPins || !onPinSelect || total === 0) {
        return;
      }
      const prevIndex = (currentIndex - 1 + total) % total;
      const id = nearbyPins[prevIndex]?.properties.externalId;
      if (id) {
        onPinSelect(id);
      }
      analytics.event("game_map_nearby_pins_navigate", {
        mapId: currentMapInfo?.externalId ?? "",
        direction: "previous",
        platform: hostType
      });
    };
    $[4] = analytics;
    $[5] = currentIndex;
    $[6] = currentMapInfo?.externalId;
    $[7] = hostType;
    $[8] = nearbyPins;
    $[9] = onPinSelect;
    $[10] = total;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handlePrev = t3;
  let t4;
  if ($[12] !== analytics || $[13] !== currentIndex || $[14] !== currentMapInfo?.externalId || $[15] !== hostType || $[16] !== nearbyPins || $[17] !== onPinSelect || $[18] !== total) {
    t4 = () => {
      if (!nearbyPins || !onPinSelect || total === 0) {
        return;
      }
      const nextIndex = (currentIndex + 1) % total;
      const id_0 = nearbyPins[nextIndex]?.properties.externalId;
      if (id_0) {
        onPinSelect(id_0);
      }
      analytics.event("game_map_nearby_pins_navigate", {
        mapId: currentMapInfo?.externalId ?? "",
        direction: "next",
        platform: hostType
      });
    };
    $[12] = analytics;
    $[13] = currentIndex;
    $[14] = currentMapInfo?.externalId;
    $[15] = hostType;
    $[16] = nearbyPins;
    $[17] = onPinSelect;
    $[18] = total;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const handleNext = t4;
  let t5;
  if ($[20] !== currentIndex || $[21] !== handleNext || $[22] !== handlePrev || $[23] !== nearbyPins || $[24] !== onPinSelect || $[25] !== t || $[26] !== total) {
    t5 = nearbyPins && nearbyPins.length > 1 && currentIndex >= 0 && onPinSelect && <FlexRow align="center" justify="between" width="full" className={css.raw({
      flexShrink: "0",
      paddingX: "16",
      paddingY: "12",
      borderTop: "[1px solid]",
      borderColor: "whiteAlpha.10"
    })} gap="8"><IconButton onClick={handlePrev} color="secondary" size="sm" aria-label={t(_temp)}><Icon icon="arrow_back" decorative={true} /></IconButton><Type variant="caption" color="whiteAlpha.60">{currentIndex + 1}/{total} {t(_temp2)}</Type><IconButton onClick={handleNext} color="secondary" size="sm" aria-label={t(_temp3)}><Icon icon="arrow_forward" decorative={true} /></IconButton></FlexRow>;
    $[20] = currentIndex;
    $[21] = handleNext;
    $[22] = handlePrev;
    $[23] = nearbyPins;
    $[24] = onPinSelect;
    $[25] = t;
    $[26] = total;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  const nearbyPinsSection = t5;
  let t6;
  if ($[28] !== isExploreActionMenu || $[29] !== isMobilePane || $[30] !== nearbyPins) {
    t6 = !isExploreActionMenu && <LocationInfo locationStyleOverride={{
      width: "full",
      background: "transparent",
      boxShadow: "[none]",
      ...(isMobilePane && {
        _compact: {
          maxWidth: "full"
        }
      }),
      smDown: {
        marginX: "0",
        maxWidth: "full",
        width: "full"
      }
    }} isMobilePane={isMobilePane} hideActions={true} nearbyPins={nearbyPins} />;
    $[28] = isExploreActionMenu;
    $[29] = isMobilePane;
    $[30] = nearbyPins;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  const t7 = isExploreActionMenu ? "none" : "12";
  const t8 = isExploreActionMenu ? "none" : "16";
  let t9;
  if ($[32] !== t7 || $[33] !== t8) {
    t9 = css({
      paddingTop: t7,
      paddingBottom: t8,
      flexShrink: "0",
      width: "full"
    });
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  const t10 = gameCoords ?? undefined;
  let t11;
  if ($[35] !== isMobilePane || $[36] !== t10) {
    t11 = <LocationActions gameCoords={t10} isMobilePane={isMobilePane} />;
    $[35] = isMobilePane;
    $[36] = t10;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== t11 || $[39] !== t9) {
    t12 = <div className={t9}>{t11}</div>;
    $[38] = t11;
    $[39] = t9;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== isExploreActionMenu || $[42] !== selectedPinId) {
    t13 = !isExploreActionMenu && selectedPinId && <HeroActionDrawer heroOnly={true} bottomPlacement={true} selectedPinId={selectedPinId} isMobilePane={true} />;
    $[41] = isExploreActionMenu;
    $[42] = selectedPinId;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  const t14 = !isExploreActionMenu && nearbyPinsSection;
  let t15;
  if ($[44] !== t12 || $[45] !== t13 || $[46] !== t14 || $[47] !== t6) {
    t15 = <FlexColumn gap="0" width="full" height="full">{t6}{t12}{t13}{t14}</FlexColumn>;
    $[44] = t12;
    $[45] = t13;
    $[46] = t14;
    $[47] = t6;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  return t15;
};
function _temp($) {
  return $.back;
}
function _temp2($_0) {
  return $_0.pins_nearby;
}
function _temp3($_1) {
  return $_1.forward;
}